/**
 * Custom Element: <prazo-entrega>
 * Dynamically calculates and renders estimated delivery dates on every page load
 * based on the user's current date, business days / calendar days, and cutoff hour.
 */
class PrazoEntrega extends HTMLElement {
  constructor() {
    super();
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  connectedCallback() {
    this.init();
    this.setupTooltip();
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  static get observedAttributes() {
    return [
      'data-min-days',
      'data-max-days',
      'data-counting-mode',
      'data-enable-cutoff',
      'data-cutoff-hour',
      'data-date-format',
      'data-prefix',
      'data-connector',
      'data-bold-dates'
    ];
  }

  attributeChangedCallback() {
    this.init();
  }

  init() {
    const minDays = parseInt(this.getAttribute('data-min-days') || '1', 10);
    const maxDays = parseInt(this.getAttribute('data-max-days') || '7', 10);
    const countingMode = this.getAttribute('data-counting-mode') || 'dias_corridos';
    const enableCutoff = this.getAttribute('data-enable-cutoff') === 'true';
    const cutoffHour = parseInt(this.getAttribute('data-cutoff-hour') || '14', 10);
    const dateFormat = this.getAttribute('data-date-format') || 'short_month';
    const prefix = this.getAttribute('data-prefix') || 'Chegará entre';
    const connector = this.getAttribute('data-connector') || 'e';
    const boldDates = this.getAttribute('data-bold-dates') !== 'false';

    const now = new Date();
    const baseDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Se a hora atual já passou do horário de corte, adianta a data base em +1 dia
    if (enableCutoff && now.getHours() >= cutoffHour) {
      if (countingMode === 'dias_uteis') {
        baseDate.setDate(baseDate.getDate() + 1);
        while (baseDate.getDay() === 0 || baseDate.getDay() === 6) {
          baseDate.setDate(baseDate.getDate() + 1);
        }
      } else {
        baseDate.setDate(baseDate.getDate() + 1);
      }
    }

    const minDate = this.calculateTargetDate(baseDate, minDays, countingMode);
    const maxDate = this.calculateTargetDate(baseDate, maxDays, countingMode);

    const minFormatted = this.formatDate(minDate, dateFormat);
    const maxFormatted = this.formatDate(maxDate, dateFormat);

    const textSlot = this.querySelector('.prazo-entrega__text');
    if (textSlot) {
      if (boldDates) {
        textSlot.innerHTML = `${this.escapeHtml(prefix)} <strong>${this.escapeHtml(minFormatted)}</strong> ${this.escapeHtml(connector)} <strong>${this.escapeHtml(maxFormatted)}</strong>`;
      } else {
        textSlot.textContent = `${prefix} ${minFormatted} ${connector} ${maxFormatted}`;
      }
    }
  }

  calculateTargetDate(startDate, daysToAdd, mode) {
    const date = new Date(startDate.getTime());
    if (mode === 'dias_uteis') {
      let added = 0;
      while (added < daysToAdd) {
        date.setDate(date.getDate() + 1);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          added++;
        }
      }
      return date;
    } else {
      date.setDate(date.getDate() + daysToAdd);
      return date;
    }
  }

  formatDate(date, format) {
    const day = date.getDate();
    const dayPadded = String(day).padStart(2, '0');
    const month = date.getMonth();
    const monthPadded = String(month + 1).padStart(2, '0');
    const year = date.getFullYear();

    const shortMonths = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
    const fullMonths = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const shortWeekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    switch (format) {
      case 'short_month':
        return `${day} de ${shortMonths[month]}`;
      case 'full_month':
        return `${day} de ${fullMonths[month]}`;
      case 'numeric':
        return `${dayPadded}/${monthPadded}`;
      case 'numeric_year':
        return `${dayPadded}/${monthPadded}/${year}`;
      case 'weekday_short':
        return `${shortWeekdays[date.getDay()]}, ${day} de ${shortMonths[month]}`;
      default:
        return `${day} de ${shortMonths[month]}`;
    }
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  setupTooltip() {
    const infoWrap = this.querySelector('.prazo-entrega__info-wrap');
    if (!infoWrap) return;

    infoWrap.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = infoWrap.classList.toggle('is-active');
      infoWrap.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    infoWrap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const isOpen = infoWrap.classList.toggle('is-active');
        infoWrap.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      } else if (e.key === 'Escape') {
        infoWrap.classList.remove('is-active');
        infoWrap.setAttribute('aria-expanded', 'false');
      }
    });
  }

  handleDocumentClick(e) {
    const infoWrap = this.querySelector('.prazo-entrega__info-wrap');
    if (infoWrap && !infoWrap.contains(e.target)) {
      infoWrap.classList.remove('is-active');
      infoWrap.setAttribute('aria-expanded', 'false');
    }
  }
}

if (!customElements.get('prazo-entrega')) {
  customElements.define('prazo-entrega', PrazoEntrega);
}
